import React, { type FC } from 'react';
import { Field } from 'react-final-form';

import { Input, Radio, Select } from 'antd';
import TextArea from 'antd/es/input/TextArea';
import dayjs from 'dayjs';

import FieldWrapper from 'components/FieldWrapper';
import FormSwitch from 'components/FormSwitch';
import FormBlockLine from 'modules/Common/components/FormBlockLine';
import { RadioOptions, TimeOptions } from 'modules/Common/constants';
import { Fields, OptionsType } from 'types';
import { filterSelectOptions } from 'utils/helpers';
import {
	composeValidators,
	numberValidator,
	positiveIntegersValidator,
	requiredFieldValidator,
	textValidator,
} from 'utils/validators';

import { Styled } from './ContractDetails.styled';

type ContractDetailsProps = {
	clients: OptionsType[];
	priceLoading: boolean;
	clientContextId: string;
	isEditMode: boolean;
	sendCopyToRecipientEmailsField: boolean;
	lastTimeContractSent?: string | null;
	handleSendCopyToRecipientEmailsFieldChange: (value: boolean) => void;
};

export const ContractDetails: FC<ContractDetailsProps> = ({
	clients,
	priceLoading,
	clientContextId,
	isEditMode,
	sendCopyToRecipientEmailsField,
	lastTimeContractSent,
	handleSendCopyToRecipientEmailsFieldChange,
}) => {
	return (
		<Styled.FormBlockWrap title='Contract Details' clientContextId={clientContextId}>
			<FormBlockLine>
				<Field name='client' validate={requiredFieldValidator}>
					{({ input, meta }) => (
						<FieldWrapper
							name='client'
							label='Client / Company Name'
							errorMessage={meta.submitFailed && meta.touched && meta.error}
						>
							<Select
								{...input}
								value={input.value || null}
								placeholder='-Select a client-'
								options={clients}
								disabled={priceLoading}
								filterOption={(inputValue, option) => filterSelectOptions(inputValue, option)}
								showSearch
							/>
						</FieldWrapper>
					)}
				</Field>
				<Field
					name='clientOfficeAddress'
					validate={composeValidators(requiredFieldValidator, textValidator)}
				>
					{({ input, meta }) => (
						<FieldWrapper
							name='clientOfficeAddress'
							label='Client Office Address'
							errorMessage={meta.submitFailed && meta.touched && meta.error}
						>
							<TextArea
								{...input}
								disabled={priceLoading}
								placeholder={'Building \nRoad \nCountry \nPostcode'}
								rows={4}
							/>
						</FieldWrapper>
					)}
				</Field>
			</FormBlockLine>
			<FormBlockLine>
				<Field name='btoReference'>
					{({ input }) => (
						<FieldWrapper
							name='btoReference'
							label='BTO Reference'
							hint='This will be auto-generated'
						>
							<Input {...input} placeholder='CODEXXXXXX' disabled autoComplete='off' />
						</FieldWrapper>
					)}
				</Field>

				<Field name='poNumber'>
					{({ input }) => (
						<FieldWrapper
							name='poNumber'
							label='PO Number'
							hint='Enter a purchase order number if required'
						>
							<Input
								{...input}
								disabled={priceLoading}
								placeholder='Enter the code'
								autoComplete='off'
							/>
						</FieldWrapper>
					)}
				</Field>
			</FormBlockLine>
			<FormBlockLine>
				<Styled.FieldsBox>
					<Field
						name='durationDays'
						validate={composeValidators(requiredFieldValidator, numberValidator)}
					>
						{({ input, meta }) => (
							<Styled.FieldWrapper>
								<FieldWrapper
									name='durationDays'
									label='Contract Duration'
									disabled={priceLoading}
									errorMessage={meta.submitFailed && meta.touched && meta.error}
								>
									<Input {...input} type={Fields.NUMBER} placeholder='e.g. 30' min={0} />
								</FieldWrapper>
							</Styled.FieldWrapper>
						)}
					</Field>
					<Field name='durationDaysTimeUnit'>
						{({ input }) => (
							<Select {...input} placeholder='-Select duration-' options={TimeOptions} />
						)}
					</Field>
				</Styled.FieldsBox>
				<Styled.FieldsBox>
					<Styled.FieldWrapper>
						<Field
							name='paymentTerm'
							validate={composeValidators(
								requiredFieldValidator,
								numberValidator,
								positiveIntegersValidator,
							)}
						>
							{({ input, meta }) => (
								<FieldWrapper
									name='paymentTerm'
									label='Payment Terms'
									errorMessage={meta.submitFailed && meta.touched && meta.error}
								>
									<Input
										{...input}
										disabled={priceLoading}
										type={Fields.NUMBER}
										placeholder='e.g. 30'
										min={0}
									/>
								</FieldWrapper>
							)}
						</Field>
					</Styled.FieldWrapper>

					<Field name='paymentTermTimeUnit'>
						{({ input }) => (
							<Select
								{...input}
								disabled={priceLoading}
								placeholder='-Select period-'
								options={TimeOptions}
							/>
						)}
					</Field>
				</Styled.FieldsBox>
			</FormBlockLine>
			<FormBlockLine>
				<Field name='recipientEmails'>
					{({ input, meta }) => (
						<>
							<FieldWrapper
								name='recipientEmails'
								label='Recipient email'
								hint={'Can be one email or many separated by commas'}
								errorMessage={meta.submitFailed && meta.touched && meta.error}
							>
								<Input
									{...input}
									disabled={!sendCopyToRecipientEmailsField}
									placeholder='email@bluetown.co.uk'
									autoComplete='off'
								/>
							</FieldWrapper>
						</>
					)}
				</Field>
				<Styled.FieldToggle marginTop={'40px'}>
					<FormSwitch
						name='sendCopyToRecipientEmails'
						title='Send'
						value={sendCopyToRecipientEmailsField}
						onChange={(value) => handleSendCopyToRecipientEmailsFieldChange(value)}
					/>
					{isEditMode && lastTimeContractSent ? (
						<Styled.FieldHint>
							Last time sent: {dayjs(lastTimeContractSent).format('MMM DD, YYYY HH:mm')}
						</Styled.FieldHint>
					) : (
						<></>
					)}
				</Styled.FieldToggle>
			</FormBlockLine>
			<FormBlockLine></FormBlockLine>
			<FormBlockLine>
				<Styled.FieldToggle>
					<Field name='getCopy'>
						{({ input }) => (
							<>
								<FormSwitch {...input} title='Get a copy' />
								<Styled.FieldHint>
									If enabled, you will receive a CC of the notification email.
								</Styled.FieldHint>
							</>
						)}
					</Field>
				</Styled.FieldToggle>
				<Styled.RadioFieldsBox>
					<Field name='chargeVat'>
						{({ input, meta }) => (
							<FieldWrapper
								name='chargeVat'
								label='Charge VAT on Contracts?'
								errorMessage={meta.submitFailed && meta.touched && meta.error}
							>
								<Radio.Group
									{...input}
									disabled={priceLoading}
									value={input.value}
									options={RadioOptions}
								/>
							</FieldWrapper>
						)}
					</Field>
					<Field name='generateInvoiceOnAcceptance'>
						{({ input, meta }) => (
							<FieldWrapper
								name='generateInvoiceOnAcceptance'
								label='Generate Invoice on Acceptance?'
								errorMessage={meta.submitFailed && meta.touched && meta.error}
							>
								<Radio.Group
									{...input}
									disabled={priceLoading}
									value={input.value}
									options={RadioOptions}
								/>
							</FieldWrapper>
						)}
					</Field>
				</Styled.RadioFieldsBox>
			</FormBlockLine>
		</Styled.FormBlockWrap>
	);
};
